import { useMemo } from "react"
import { Client, ConditionGroup, NextPageParams, PaginationMetadata } from "@/schema"
import { useDataTableStore } from "@/stores/data-table-store"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  AddeparReportResult,
  AllClientIdsResponse,
  ClientAddeparReportsResponse,
  ClientCustodianAccount,
  ClientReportingAccountsResponse,
  ClientReportingPeriod,
  ClientsResponse,
  fetchAddeparReportResult,
  fetchAllClientIds,
  fetchClient,
  fetchClientAddeparReports,
  fetchClientReportingAccounts,
  fetchClients,
  fetchPortfolioBalanceToday,
  fetchPortfolioComposition,
  fetchPortfolioCustodianAccounts,
  fetchPortfolioRealGrowth,
  fetchPortfolioTransactions,
  PortfolioBalanceTodayResponse,
  PortfolioCompositionResponse,
  PortfolioOwnerType,
  PortfolioRealGrowthResponse,
  PortfolioTransactionsResponse,
} from "./api"

export function useFetchClients(viewFilterGroup?: ConditionGroup, searchTerm?: string) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["clients", searchTerm],
    queryFn: async () => {
      const content = await getContext()
      if (!content) throw new Error("No content found")
      return fetchClients(content, viewFilterGroup, undefined, searchTerm)
    },
  })
}

export function useInfiniteClients(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, completedConditions, viewSort } = useDataTableStore()
  const computedFilterGroup = useMemo(
    () =>
      ({
        combinator: "and",
        conditions: completedConditions,
      }) as ConditionGroup,
    [completedConditions]
  )
  return useInfiniteQuery<
    ClientsResponse,
    Error,
    { clients: Client[]; pagination: PaginationMetadata; total_aum: number },
    (string | ConditionGroup | undefined | { field?: string; direction?: "asc" | "desc" })[],
    NextPageParams | undefined
  >({
    queryKey: ["clients", "infinite", searchTerm, computedFilterGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const content = await getContext()
      if (!content) throw new Error("No content found")

      return fetchClients(content, computedFilterGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      clients: data.pages.flatMap((page) => page.clients),
      pagination: data.pages[data.pages.length - 1]?.pagination,
      total_aum: data.pages[data.pages.length - 1]?.total_aum,
    }),
  })
}

export function useFetchClient(id: string | null) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["client", id],
    enabled: !!id,
    queryFn: async () => {
      const content = await getContext()
      if (!content) throw new Error("No content found")
      if (!id) throw new Error("No id found")
      return fetchClient(content, id)
    },
  })
}

export function useFetchAllClientIds(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, completedConditions, viewSort } = useDataTableStore()
  const computedFilterGroup = useMemo(
    () =>
      ({
        combinator: "and",
        conditions: completedConditions,
      }) as ConditionGroup,
    [completedConditions]
  )

  return useQuery<AllClientIdsResponse>({
    queryKey: ["clients", "all_ids", searchTerm, computedFilterGroup, viewSort],
    queryFn: async () => {
      const content = await getContext()
      if (!content) throw new Error("No content found")
      return fetchAllClientIds(content, computedFilterGroup, searchTerm)
    },
    enabled,
  })
}

export function useFetchPortfolioComposition(
  ownerId: string | null,
  options?: { enabled?: boolean; accountIds?: string[]; ownerType?: PortfolioOwnerType }
) {
  const getContext = useApiContext()
  const sortedAccountIds = options?.accountIds ? [...options.accountIds].sort() : undefined
  return useQuery({
    queryKey: [options?.ownerType ?? "clients", ownerId, "portfolio_composition", sortedAccountIds],
    enabled: options?.enabled ?? !!ownerId,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!ownerId) throw new Error("No portfolio owner id")
      const raw = await fetchPortfolioComposition(context, ownerId, sortedAccountIds, options?.ownerType)

      const composition = Object.entries(raw.composition ?? {}).reduce<PortfolioCompositionResponse["composition"]>(
        (acc, [symbol, payload]) => {
          acc[symbol] = {
            absolute_value: payload.absolute_value,
            percentage: payload.percentage,
            asset_class: payload.asset_class ?? payload.type ?? payload.security_type ?? null,
            type: payload.type ?? payload.security_type ?? null,
            security_type: payload.security_type ?? null,
            description: payload.description ?? null,
            equity_percentage: payload.equity_percentage ?? null,
            fixed_income_percentage: payload.fixed_income_percentage ?? null,
            dominant_class: payload.dominant_class ?? null,
          }
          return acc
        },
        {}
      )

      const assetClassComposition = Object.entries(raw.asset_class_composition ?? {}).reduce<
        PortfolioCompositionResponse["asset_class_composition"]
      >((acc, [assetClass, payload]) => {
        acc[assetClass] = {
          absolute_value: payload.absolute_value,
          percentage: payload.percentage,
        }
        return acc
      }, {})

      return {
        composition,
        asset_class_composition: assetClassComposition,
        total_value: raw.total_value,
        cost_basis: raw.cost_basis ?? null,
        unrealized_gain_loss: raw.unrealized_gain_loss ?? null,
        pricing_status: raw.pricing_status ?? null,
        positions_as_of: raw.positions_as_of ?? null,
      } satisfies PortfolioCompositionResponse
    },
  })
}

export function useFetchPortfolioBalanceToday(
  ownerId: string | null,
  options?: { enabled?: boolean; accountIds?: string[]; ownerType?: PortfolioOwnerType }
) {
  const getContext = useApiContext()
  const sortedAccountIds = options?.accountIds ? [...options.accountIds].sort() : undefined
  return useQuery<PortfolioBalanceTodayResponse>({
    queryKey: [options?.ownerType ?? "clients", ownerId, "portfolio_balance_today", sortedAccountIds],
    enabled: options?.enabled ?? !!ownerId,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!ownerId) throw new Error("No portfolio owner id")
      return fetchPortfolioBalanceToday(context, ownerId, sortedAccountIds, options?.ownerType)
    },
  })
}

export function useFetchPortfolioTransactions(
  ownerId: string | null,
  options?: {
    limit?: number
    lookbackDays?: number
    enabled?: boolean
    accountIds?: string[]
    ownerType?: PortfolioOwnerType
  }
) {
  const getContext = useApiContext()
  const sortedAccountIds = options?.accountIds ? [...options.accountIds].sort() : undefined
  return useQuery<PortfolioTransactionsResponse>({
    queryKey: [
      options?.ownerType ?? "clients",
      ownerId,
      "portfolio_transactions",
      options?.limit,
      options?.lookbackDays,
      sortedAccountIds,
    ],
    enabled: options?.enabled ?? !!ownerId,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!ownerId) throw new Error("No portfolio owner id")
      return fetchPortfolioTransactions(
        context,
        ownerId,
        {
          limit: options?.limit,
          lookback_days: options?.lookbackDays,
          account_ids: sortedAccountIds,
        },
        options?.ownerType
      )
    },
  })
}

export function useFetchPortfolioCustodianAccounts(
  ownerId: string | null,
  options?: { enabled?: boolean; ownerType?: PortfolioOwnerType }
) {
  const getContext = useApiContext()
  return useQuery<ClientCustodianAccount[]>({
    queryKey: [options?.ownerType ?? "clients", ownerId, "custodian_accounts"],
    enabled: options?.enabled ?? !!ownerId,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!ownerId) throw new Error("No portfolio owner id")
      return fetchPortfolioCustodianAccounts(context, ownerId, options?.ownerType)
    },
  })
}

export function useFetchClientReportingAccounts(
  clientId: string | null,
  period: ClientReportingPeriod,
  enabled = true
) {
  const getContext = useApiContext()
  return useQuery<ClientReportingAccountsResponse>({
    queryKey: ["clients", clientId, "reporting_accounts", period],
    enabled: enabled && !!clientId,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!clientId) throw new Error("No client id")
      return fetchClientReportingAccounts(context, clientId, period)
    },
  })
}

export function useFetchPortfolioRealGrowth(
  ownerId: string | null,
  options?: { enabled?: boolean; accountIds?: string[]; ownerType?: PortfolioOwnerType }
) {
  const getContext = useApiContext()
  const sortedAccountIds = options?.accountIds ? [...options.accountIds].sort() : undefined
  return useQuery<PortfolioRealGrowthResponse>({
    queryKey: [options?.ownerType ?? "clients", ownerId, "portfolio_real_growth", sortedAccountIds],
    enabled: options?.enabled ?? !!ownerId,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!ownerId) throw new Error("No portfolio owner id")
      return fetchPortfolioRealGrowth(context, ownerId, sortedAccountIds, options?.ownerType)
    },
  })
}

export function useFetchClientPortfolioComposition(
  clientId: string | null,
  options?: { enabled?: boolean; accountIds?: string[] }
) {
  return useFetchPortfolioComposition(clientId, { ...options, ownerType: "clients" })
}

export function useFetchClientPortfolioBalanceToday(
  clientId: string | null,
  options?: { enabled?: boolean; accountIds?: string[] }
) {
  return useFetchPortfolioBalanceToday(clientId, { ...options, ownerType: "clients" })
}

export function useFetchClientPortfolioTransactions(
  clientId: string | null,
  options?: { limit?: number; lookbackDays?: number; enabled?: boolean; accountIds?: string[] }
) {
  return useFetchPortfolioTransactions(clientId, { ...options, ownerType: "clients" })
}

export function useFetchClientCustodianAccounts(clientId: string | null, options?: { enabled?: boolean }) {
  return useFetchPortfolioCustodianAccounts(clientId, { ...options, ownerType: "clients" })
}

export function useFetchClientPortfolioRealGrowth(
  clientId: string | null,
  options?: { enabled?: boolean; accountIds?: string[] }
) {
  return useFetchPortfolioRealGrowth(clientId, { ...options, ownerType: "clients" })
}

export function useFetchClientAddeparReports(clientId: string | null, enabled = true, reportingHouseholdId?: string) {
  const getContext = useApiContext()
  return useQuery<ClientAddeparReportsResponse>({
    queryKey: ["clients", clientId, "addepar_reports", reportingHouseholdId],
    enabled: enabled && !!clientId,
    staleTime: 60 * 60 * 1000,
    refetchInterval: (query) =>
      query.state.data?.addepar_reports.some((report) => report.result === null) ? 15 * 1000 : false,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!clientId) throw new Error("No client id")
      return fetchClientAddeparReports(context, clientId, reportingHouseholdId)
    },
  })
}

export function useFetchAddeparReportResult(resultId: string | null) {
  const getContext = useApiContext()
  return useQuery<AddeparReportResult>({
    queryKey: ["addepar_report_result", resultId],
    enabled: !!resultId,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!resultId) throw new Error("No report result id")
      return fetchAddeparReportResult(context, resultId)
    },
  })
}
